import React, { useState, useRef, useEffect } from "react";
import { generateResponse } from "./Flash";
import "./ChatBot.css";
import chatIcon from './assets/chatIcon.webp';
import { ChatBotProps, Message } from "./ChatBot.d";

const ChatBot: React.FC<ChatBotProps> = ({
  primaryColor = "#3b82f6",
  secondaryColor = "#ffffff",
  textColor = "#1e293b",
  position = "bottom-right",
  initialMessages = [],
  title = "AI Assistant",
  placeholder = "Ask me anything...",
  showAvatar = true,
  avatarUrl = null,
  borderRadius = "16px",
  shadow = "0 20px 40px rgba(0, 0, 0, 0.15)",
  enableAI = true,
  systemPrompt = "You are a helpful AI assistant. Be friendly, concise, and helpful in your responses.",
  API_KEY = "AIzaSyBp7sxTnBEa3MccUiSmhefyLIWgDDsm7f8",
  chatIcon: customChatIcon = chatIcon,
  inputBackgroundColor = null,
  inputTextColor = null,
  inputBorderColor = null,
  inputPlaceholderColor = null,
  botIcon = "🤖",
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState<string>("");
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  if (!API_KEY) {
    throw new Error("API_KEY is required");
  }

  // Generate color palette based on primary color
  const generateColorPalette = (primary: string) => {
    // Check if primary is a gradient
    const isGradient = primary.includes('gradient') || primary.includes('linear-gradient');
    
    if (isGradient) {
      // Handle case where primary and secondary colors are the same
      const isSameColor = primary === secondaryColor;
      const effectiveSecondary = isSameColor ? "#ffffff" : secondaryColor;
      const effectiveTextOnPrimary = isSameColor ? "#ffffff" : secondaryColor;

      return {
        primary: primary,
        primaryLight: `rgba(102, 126, 234, 0.1)`, // Default purple for gradients
        primaryLighter: `rgba(102, 126, 234, 0.05)`,
        primaryDark: `rgba(82, 106, 214, 1)`,
        primaryHover: `rgba(122, 146, 254, 1)`,
        textOnPrimary: effectiveTextOnPrimary,
        background: effectiveSecondary,
        messageBackground: `rgba(102, 126, 234, 0.1)`,
        borderColor: `rgba(102, 126, 234, 0.2)`,
        inputFocus: `rgba(102, 126, 234, 0.1)`,
      };
    }

    // Handle hex colors
    const hex = primary.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Handle case where primary and secondary colors are the same
    const isSameColor = primary === secondaryColor;
    const effectiveSecondary = isSameColor ? "#ffffff" : secondaryColor;
    const effectiveTextOnPrimary = isSameColor ? "#ffffff" : secondaryColor;

    return {
      primary: primary,
      primaryLight: `rgba(${r}, ${g}, ${b}, 0.1)`,
      primaryLighter: `rgba(${r}, ${g}, ${b}, 0.05)`,
      primaryDark: `rgba(${Math.max(0, r - 30)}, ${Math.max(
        0,
        g - 30
      )}, ${Math.max(0, b - 30)}, 1)`,
      primaryHover: `rgba(${Math.min(255, r + 20)}, ${Math.min(
        255,
        g + 20
      )}, ${Math.min(255, b + 20)}, 1)`,
      textOnPrimary: effectiveTextOnPrimary,
      background: effectiveSecondary,
      messageBackground: isSameColor ? `rgba(${r}, ${g}, ${b}, 0.15)` : `rgba(${r}, ${g}, ${b}, 0.1)`,
      borderColor: isSameColor ? `rgba(${r}, ${g}, ${b}, 0.3)` : `rgba(${r}, ${g}, ${b}, 0.2)`,
      inputFocus: `rgba(${r}, ${g}, ${b}, 0.1)`,
    };
  };

  const colors = generateColorPalette(primaryColor);

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSendMessage = async (): Promise<void> => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now(),
      text: inputValue,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue("");
    setIsTyping(true);

    if (enableAI) {
      try {
        // Create a context-aware prompt with system prompt and conversation history
        const conversationHistory = messages
          .slice(-5)
          .map(
            (msg) =>
              `${msg.sender === "user" ? "User" : "Assistant"}: ${msg.text}`
          )
          .join("\n");

        const fullPrompt = `${systemPrompt}\n\nConversation History:\n${conversationHistory}\n\nUser: ${currentInput}\nAssistant:`;

        // Call the Flash API
        const response = await generateResponse(fullPrompt, API_KEY);

        // Extract the response text from the API response
        let botResponseText =
          "Sorry, I couldn't generate a response. Please try again.";

        if (
          response &&
          response.candidates &&
          response.candidates[0] &&
          response.candidates[0].content
        ) {
          botResponseText = response.candidates[0].content.parts[0].text;
        }

        const botMessage: Message = {
          id: Date.now() + 1,
          text: botResponseText,
          sender: "bot",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botMessage]);
      } catch (error) {
        console.error("Error getting AI response:", error);

        // Show error message to user
        const errorMessage: Message = {
          id: Date.now() + 1,
          text: "Sorry, I'm having trouble connecting to the AI service. Please try again later.",
          sender: "bot",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsTyping(false);
      }
    } else {
      // Fallback to simple response if AI is disabled
      setTimeout(() => {
        const botMessage: Message = {
          id: Date.now() + 1,
          text: `I received your message: "${currentInput}". How can I help you further?`,
          sender: "bot",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMessage]);
        setIsTyping(false);
      }, 1000);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getPositionStyles = (): React.CSSProperties => {
    const positions: Record<string, React.CSSProperties> = {
      "bottom-right": { bottom: "20px", right: "20px" },
      "bottom-left": { bottom: "20px", left: "20px" },
      "top-right": { top: "20px", right: "20px" },
      "top-left": { top: "20px", left: "20px" },
      center: { top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
    };
    return positions[position] || positions["bottom-right"];
  };

  const formatTime = (timestamp: Date): string => {
    return timestamp.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="chatbot-container" style={getPositionStyles()}>
      {/* Chat Window */}
      {isOpen && (
        <div
          className="chatbot-window"
          style={{
            background: `linear-gradient(135deg, ${colors.background} 0%, ${colors.primaryLighter} 100%)`,
            borderRadius: borderRadius,
            boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)`,
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          {/* Header */}
          <div
            className="chatbot-header"
            style={{
              background: colors.primary.includes('gradient') 
                ? colors.primary 
                : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Animated background pattern */}
            <div className="chatbot-header-pattern"></div>
            <div className="chatbot-header-content">
              {showAvatar && (
                <div className="chatbot-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Bot Avatar" />
                  ) : (
                    <div
                      className="chatbot-avatar-placeholder"
                      style={{
                        background: `linear-gradient(135deg, ${colors.background} 0%, ${colors.primaryLighter} 100%)`,
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <div className="avatar-icon">
                        <img src={customChatIcon} alt="AI" />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="chatbot-title">
                <h3
                  style={{
                    color: colors.textOnPrimary,
                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {title}
                </h3>
                <div className="chatbot-status-container">
                  <div className="status-dot"></div>
                  <span
                    className="chatbot-status"
                    style={{ color: colors.textOnPrimary }}
                  >
                    Online
                  </span>
                </div>
              </div>
            </div>
            <button
              className="chatbot-close"
              onClick={() => setIsOpen(false)}
              title="Close chat"
              style={{
                color: colors.textOnPrimary,
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>

          {/* Messages Area */}
          <div
            className="chatbot-messages"
            style={{
              background: `linear-gradient(180deg, ${colors.primaryLighter} 0%, ${colors.background} 100%)`,
              position: "relative",
            }}
          >
            {/* Subtle pattern overlay */}
            <div className="messages-pattern"></div>

            {messages.length === 0 ? (
              <div className="chatbot-welcome">
                <div className="welcome-icon">👋</div>
                <h4
                  style={{
                    color: textColor,
                    margin: "0 0 8px 0",
                    fontWeight: "600",
                  }}
                >
                  Hello!
                </h4>
                <p style={{ color: textColor, margin: 0, opacity: 0.8 }}>
                  How can I help you today?
                </p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`chatbot-message ${message.sender}`}
                >
                  {message.sender === "bot" && showAvatar && (
                    <div 
                      className="message-avatar"
                      style={{
                        background: colors.primary.includes('gradient') 
                          ? colors.primary 
                          : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                        borderRadius: '50%',
                        padding: '2px',
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      }}
                    >
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="Bot Avatar" />
                      ) : botIcon.includes('.') || botIcon.includes('/') || botIcon.includes('http') ? (
                        <img src={botIcon} alt="Bot Icon" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                      ) : (
                        <div
                          className="avatar-placeholder"
                          style={{
                            background: colors.primary.includes('gradient') 
                              ? colors.primary 
                              : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          }}
                        >
                          {botIcon}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="message-content">
                    <div
                      className="message-bubble"
                      style={{
                        background:
                          message.sender === "user"
                            ? (colors.primary.includes('gradient') 
                                ? colors.primary 
                                : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`)
                            : `linear-gradient(135deg, ${colors.messageBackground} 0%, rgba(255, 255, 255, 0.8) 100%)`,
                        color:
                          message.sender === "user"
                            ? colors.textOnPrimary
                            : textColor,
                        boxShadow:
                          message.sender === "user"
                            ? "0 4px 15px rgba(0, 0, 0, 0.1)"
                            : "0 2px 8px rgba(0, 0, 0, 0.05)",
                        border:
                          message.sender === "user"
                            ? "none"
                            : "1px solid rgba(255, 255, 255, 0.3)",
                      }}
                    >
                      {message.text}
                    </div>
                    <div
                      className="message-time"
                      style={{ color: textColor, opacity: 0.7 }}
                    >
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                </div>
              ))
            )}

            {isTyping && (
              <div className="chatbot-message bot">
                {showAvatar && (
                  <div 
                    className="message-avatar"
                    style={{
                      background: colors.primary.includes('gradient') 
                        ? colors.primary 
                        : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                      borderRadius: '50%',
                      padding: '2px',
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    }}
                  >
                    {botIcon.includes('.') || botIcon.includes('/') || botIcon.includes('http') ? (
                      <img src={botIcon} alt="Bot Icon" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    ) : (
                      <div
                        className="avatar-placeholder"
                        style={{
                          background: colors.primary.includes('gradient') 
                            ? colors.primary 
                            : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        }}
                      >
                        {botIcon}
                      </div>
                    )}
                  </div>
                )}
                <div className="message-content">
                  <div
                    className="typing-indicator"
                    style={{
                      background: `linear-gradient(135deg, ${colors.messageBackground} 0%, rgba(255, 255, 255, 0.8) 100%)`,
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <span style={{ backgroundColor: colors.primary }}></span>
                    <span style={{ backgroundColor: colors.primary }}></span>
                    <span style={{ backgroundColor: colors.primary }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div
            className="chatbot-input-area"
            style={{
              background: `linear-gradient(180deg, ${colors.background} 0%, ${colors.primaryLighter} 100%)`,
              borderTop: `1px solid ${colors.borderColor}`,
              position: "relative",
            }}
          >
            <div className="chatbot-input-container">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={placeholder}
                className="chatbot-input"
                rows={1}
                style={{
                  border: `2px solid ${inputBorderColor || colors.borderColor}`,
                  color: inputTextColor || textColor,
                  background: inputBackgroundColor 
                    ? inputBackgroundColor 
                    : `linear-gradient(135deg, ${colors.background} 0%, rgba(255, 255, 255, 0.8) 100%)`,
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  '--placeholder-color': inputPlaceholderColor || 'rgba(0, 0, 0, 0.6)',
                } as React.CSSProperties}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim()}
                className="chatbot-send"
                title="Send message"
                style={{
                  background: inputValue.trim()
                    ? (inputBackgroundColor 
                        ? inputBackgroundColor 
                        : (colors.primary.includes('gradient') 
                            ? colors.primary 
                            : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`))
                    : `linear-gradient(135deg, ${colors.borderColor} 0%, ${colors.borderColor} 100%)`,
                  color: colors.textOnPrimary,
                  opacity: inputValue.trim() ? 1 : 0.5,
                  boxShadow: inputValue.trim()
                    ? "0 4px 15px rgba(0, 0, 0, 0.2)"
                    : "0 2px 8px rgba(0, 0, 0, 0.1)",
                  border: "none",
                  transition: "all 0.3s ease",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {!isOpen && (
        <button
          className="chatbot-toggle"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            background: colors.primary.includes('gradient') 
              ? colors.primary 
              : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
            boxShadow: `0 8px 25px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)`,
            border: "1px solid rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Animated background effect */}
          <div className="toggle-bg-effect"></div>

          {isOpen ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ zIndex: 1, position: "relative" }}
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ zIndex: 1, position: "relative" }}
            >
              <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
            </svg>
          )}

          {/* Notification badge
          {!isOpen && messages.length > 0 && (
            <div className="notification-badge">{messages.length}</div>
          )} */}
        </button>
      )}
    </div>
  );
};

export default ChatBot;
